import React, { useState, useEffect } from 'react';
import { api } from '../../src/services/api-new';

// --- Types ---
interface Vet {
    id: string;
    name: string;
    specialization: string;
    experience: number;
    phone: string;
    email: string;
    schedule: string | object;
    available: boolean;
}

interface AdminVetsProps {
    vets?: Vet[];
    onBack?: () => void;
}

const AdminVets: React.FC<AdminVetsProps> = ({ vets: externalVets, onBack }) => {
    const [vets, setVets] = useState<Vet[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingVet, setEditingVet] = useState<Vet | null>(null);
    const [isDataLoading, setIsDataLoading] = useState(true);
    const [formData, setFormData] = useState({
        name: '',
        specialization: '',
        experience: '',
        phone: '',
        email: '',
        schedule: '',
        available: true
    });

    useEffect(() => {
        const fetchData = async () => {
            if (externalVets && externalVets.length > 0) {
                setVets(externalVets as Vet[]);
                setIsDataLoading(false);
                return;
            }

            setIsDataLoading(true);
            try {
                const vetsData = await api.vets.getAll();
                setVets(vetsData);
            } catch (error) {
                console.error("Failed to fetch vets:", error);
            } finally {
                setIsDataLoading(false);
            }
        };
        fetchData();
    }, [externalVets]);

    const handleAddVet = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const result = await api.vets.create({
                name: formData.name,
                specialization: formData.specialization,
                experience: parseInt(formData.experience),
                phone: formData.phone,
                email: formData.email,
                schedule: formData.schedule,
                available: formData.available
            });
            const newVet = result.vet || result;
            setVets([...vets, newVet]);
            setIsAddModalOpen(false);
            resetForm();
        } catch (error) {
            console.error("Failed to add vet:", error);
            alert("Failed to add veterinarian. Please try again.");
        }
    };

    const handleEditVet = (vet: Vet) => {
        setEditingVet(vet);
        setFormData({
            name: vet.name,
            specialization: vet.specialization,
            experience: vet.experience.toString(),
            phone: vet.phone,
            email: vet.email,
            schedule: typeof vet.schedule === 'object' ? JSON.stringify(vet.schedule) : vet.schedule,
            available: vet.available
        });
        setIsEditModalOpen(true);
    };

    const handleUpdateVet = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingVet) return;

        try {
            await api.vets.update(editingVet.id, {
                name: formData.name,
                specialization: formData.specialization,
                experience: parseInt(formData.experience),
                phone: formData.phone,
                email: formData.email,
                schedule: formData.schedule,
                available: formData.available
            });

            const updatedVets = vets.map(vet =>
                vet.id === editingVet.id
                    ? { ...vet, ...formData, experience: parseInt(formData.experience), available: formData.available }
                    : vet
            );
            setVets(updatedVets);
            setIsEditModalOpen(false);
            setEditingVet(null);
            resetForm();
        } catch (error) {
            console.error("Failed to update vet:", error);
        }
    };

    const handleDeleteVet = async (id: string) => {
        if (!confirm('Are you sure you want to delete this veterinarian?')) return;

        try {
            await api.vets.delete(id);
            setVets(vets.filter(vet => vet.id !== id));
        } catch (error) {
            console.error("Failed to delete vet:", error);
        }
    };

    const resetForm = () => {
        setFormData({ name: '', specialization: '', experience: '', phone: '', email: '', schedule: '', available: true });
    };

    const filteredVets = vets.filter(vet =>
        vet.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vet.specialization.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vet.phone.includes(searchQuery) ||
        vet.email.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (isDataLoading) {
        return (
            <div className="h-full flex items-center justify-center bg-slate-50 dark:bg-background-dark">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
                    <p className="text-[10px] font-black text-primary uppercase tracking-[0.2em]">Loading Vets...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full overflow-y-auto bg-slate-50 dark:bg-background-dark p-6 lg:p-10 scroll-smooth">
            <div className="max-w-7xl mx-auto space-y-10">

                {/* Header */}
                <header className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div className="flex items-center gap-4">
                        {onBack && (
                            <button
                                onClick={onBack}
                                className="flex items-center justify-center w-10 h-10 rounded-full bg-white dark:bg-surface-dark border border-gray-100 dark:border-gray-800 hover:border-primary transition-all shadow-sm"
                            >
                                <span className="material-icons-round text-gray-600 dark:text-gray-300">arrow_back</span>
                            </button>
                        )}
                        <div>
                            <nav className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">
                                <span>Admin Panel</span>
                                <span className="material-icons-round text-xs">chevron_right</span>
                                <span>Vets Management</span>
                            </nav>
                            <h1 className="text-4xl font-black text-primary-dark dark:text-white tracking-tight uppercase">Veterinarians</h1>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setIsAddModalOpen(true)}
                            className="bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-primary/30 transition-all flex items-center gap-2 active:scale-95"
                        >
                            <span className="material-icons-round text-sm">add</span> Add Vet
                        </button>
                    </div>
                </header>

                {/* Search Bar */}
                <div className="bg-white dark:bg-surface-dark p-6 rounded-[2.5rem] shadow-xl shadow-black/5 border border-gray-100 dark:border-gray-800">
                    <div className="relative">
                        <span className="material-icons-round absolute left-6 top-1/2 -translate-y-1/2 text-gray-400">search</span>
                        <input
                            type="text"
                            placeholder="Search vets by name, specialization, phone, or email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-16 pr-6 py-4 bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white dark:focus:bg-surface-dark rounded-2xl text-sm font-medium outline-none transition-all"
                        />
                    </div>
                </div>

                {/* Vets Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredVets.map((vet) => (
                        <div key={vet.id} className="bg-white dark:bg-surface-dark p-6 rounded-[2.5rem] shadow-xl shadow-black/5 border border-gray-100 dark:border-gray-800 hover:border-primary/50 transition-all group">
                            <div className="flex justify-between items-start mb-4">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                                        <span className="material-icons-round text-primary">person</span>
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-black text-primary-dark dark:text-white tracking-tight">{vet.name}</h3>
                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{vet.specialization}</p>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleEditVet(vet)}
                                        className="w-8 h-8 rounded-xl bg-primary/10 text-primary hover:bg-primary/20 transition-all flex items-center justify-center"
                                    >
                                        <span className="material-icons-round text-sm">edit</span>
                                    </button>
                                    <button
                                        onClick={() => handleDeleteVet(vet.id)}
                                        className="w-8 h-8 rounded-xl bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-all flex items-center justify-center"
                                    >
                                        <span className="material-icons-round text-sm">delete</span>
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-3 mb-4">
                                <div className="flex items-center gap-3">
                                    <span className="material-icons-round text-gray-400 text-sm">phone</span>
                                    <span className="text-sm text-gray-600 dark:text-gray-300 font-medium">{vet.phone}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="material-icons-round text-gray-400 text-sm">email</span>
                                    <span className="text-sm text-gray-600 dark:text-gray-300 font-medium">{vet.email}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="material-icons-round text-gray-400 text-sm">schedule</span>
                                    <span className="text-sm text-gray-600 dark:text-gray-300 font-medium">{vet.experience} years experience</span>
                                </div>
                            </div>

                            <div className="flex justify-between items-center pt-4 border-t border-gray-100 dark:border-gray-800">
                                <div className="flex items-center gap-2">
                                    <div className={`w-2 h-2 rounded-full ${vet.available ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                        {vet.available ? 'Available' : 'Unavailable'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {filteredVets.length === 0 && (
                    <div className="text-center py-12">
                        <span className="material-icons-round text-6xl text-gray-300 mb-4">person</span>
                        <h3 className="text-lg font-black text-gray-400 uppercase tracking-widest mb-2">No Vets Found</h3>
                        <p className="text-sm text-gray-500">{searchQuery ? 'Try adjusting your search terms' : 'No veterinarians have been added yet'}</p>
                    </div>
                )}
            </div>

            {/* Add Vet Modal */}
            {isAddModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-primary-dark/40 backdrop-blur-xl animate-fade-in" onClick={() => setIsAddModalOpen(false)}>
                    <div className="bg-white dark:bg-surface-dark w-full max-w-md rounded-[3rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-scale-up" onClick={e => e.stopPropagation()}>
                        <div className="p-10 border-b border-gray-100 dark:border-gray-800 bg-slate-50/50 dark:bg-black/20 flex justify-between items-center">
                            <div>
                                <p className="text-[10px] font-black text-primary uppercase tracking-[0.3em] mb-1">New Entry</p>
                                <h2 className="text-3xl font-black text-primary-dark dark:text-white tracking-tight">Add Veterinarian</h2>
                            </div>
                            <button onClick={() => setIsAddModalOpen(false)} className="w-12 h-12 rounded-2xl hover:bg-white dark:hover:bg-gray-700 transition-colors flex items-center justify-center text-gray-400"><span className="material-icons-round">close</span></button>
                        </div>

                        <form id="add-vet-form" onSubmit={handleAddVet} className="flex-1 overflow-y-auto p-10 space-y-6 custom-scrollbar">
                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Full Name</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-lg font-black outline-none transition-all placeholder:text-gray-300"
                                    placeholder="Dr. Jane Smith"
                                />
                            </div>

                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Specialization</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.specialization}
                                    onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                                    className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-lg font-black outline-none transition-all placeholder:text-gray-300"
                                    placeholder="e.g. Small Animal Medicine"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Phone</label>
                                    <input
                                        type="tel"
                                        required
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-sm font-black outline-none transition-all"
                                        placeholder="(555) 123-4567"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Experience (years)</label>
                                    <input
                                        type="number"
                                        required
                                        value={formData.experience}
                                        onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-sm font-black outline-none transition-all"
                                        placeholder="5"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Email</label>
                                <input
                                    type="email"
                                    required
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-sm font-black outline-none transition-all placeholder:text-gray-300"
                                    placeholder="dr.smith@clinic.com"
                                />
                            </div>

                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Schedule</label>
                                <textarea
                                    value={formData.schedule}
                                    onChange={(e) => setFormData({ ...formData, schedule: e.target.value })}
                                    rows={3}
                                    className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-sm font-medium outline-none transition-all resize-none"
                                    placeholder="Mon-Fri: 9AM-5PM, Sat: 9AM-12PM"
                                />
                            </div>

                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="available"
                                    checked={formData.available}
                                    onChange={(e) => setFormData({ ...formData, available: e.target.checked })}
                                    className="w-4 h-4 text-primary bg-slate-50 dark:bg-background-dark border-gray-300 rounded focus:ring-primary"
                                />
                                <label htmlFor="available" className="text-sm font-medium text-gray-600 dark:text-gray-300">Available for appointments</label>
                            </div>
                        </form>

                        <div className="p-10 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-4 bg-slate-50/50 dark:bg-black/20">
                            <button type="button" onClick={() => setIsAddModalOpen(false)} className="px-8 py-4 font-black text-[10px] text-gray-400 uppercase tracking-widest hover:text-gray-600 transition-colors">Cancel</button>
                            <button type="submit" form="add-vet-form" className="bg-primary text-white px-10 py-5 rounded-3xl font-black text-[10px] uppercase tracking-[0.2em] shadow-2xl shadow-primary/30 transition-all hover:-translate-y-1 active:scale-95">Add Vet</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Vet Modal */}
            {isEditModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-primary-dark/40 backdrop-blur-xl animate-fade-in" onClick={() => setIsEditModalOpen(false)}>
                    <div className="bg-white dark:bg-surface-dark w-full max-w-md rounded-[3rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-scale-up" onClick={e => e.stopPropagation()}>
                        <div className="p-10 border-b border-gray-100 dark:border-gray-800 bg-slate-50/50 dark:bg-black/20 flex justify-between items-center">
                            <div>
                                <p className="text-[10px] font-black text-primary uppercase tracking-[0.3em] mb-1">Edit Entry</p>
                                <h2 className="text-3xl font-black text-primary-dark dark:text-white tracking-tight">Edit Veterinarian</h2>
                            </div>
                            <button onClick={() => setIsEditModalOpen(false)} className="w-12 h-12 rounded-2xl hover:bg-white dark:hover:bg-gray-700 transition-colors flex items-center justify-center text-gray-400"><span className="material-icons-round">close</span></button>
                        </div>

                        <form id="edit-vet-form" onSubmit={handleUpdateVet} className="flex-1 overflow-y-auto p-10 space-y-6 custom-scrollbar">
                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Full Name</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-lg font-black outline-none transition-all placeholder:text-gray-300"
                                    placeholder="Dr. Jane Smith"
                                />
                            </div>

                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Specialization</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.specialization}
                                    onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                                    className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-lg font-black outline-none transition-all placeholder:text-gray-300"
                                    placeholder="e.g. Small Animal Medicine"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Phone</label>
                                    <input
                                        type="tel"
                                        required
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-sm font-black outline-none transition-all"
                                        placeholder="(555) 123-4567"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Experience (years)</label>
                                    <input
                                        type="number"
                                        required
                                        value={formData.experience}
                                        onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-sm font-black outline-none transition-all"
                                        placeholder="5"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Email</label>
                                <input
                                    type="email"
                                    required
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-sm font-black outline-none transition-all placeholder:text-gray-300"
                                    placeholder="dr.smith@clinic.com"
                                />
                            </div>

                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Schedule</label>
                                <textarea
                                    value={formData.schedule}
                                    onChange={(e) => setFormData({ ...formData, schedule: e.target.value })}
                                    rows={3}
                                    className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-sm font-medium outline-none transition-all resize-none"
                                    placeholder="Mon-Fri: 9AM-5PM, Sat: 9AM-12PM"
                                />
                            </div>

                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="available-edit"
                                    checked={formData.available}
                                    onChange={(e) => setFormData({ ...formData, available: e.target.checked })}
                                    className="w-4 h-4 text-primary bg-slate-50 dark:bg-background-dark border-gray-300 rounded focus:ring-primary"
                                />
                                <label htmlFor="available-edit" className="text-sm font-medium text-gray-600 dark:text-gray-300">Available for appointments</label>
                            </div>
                        </form>

                        <div className="p-10 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-4 bg-slate-50/50 dark:bg-black/20">
                            <button type="button" onClick={() => setIsEditModalOpen(false)} className="px-8 py-4 font-black text-[10px] text-gray-400 uppercase tracking-widest hover:text-gray-600 transition-colors">Cancel</button>
                            <button type="submit" form="edit-vet-form" className="bg-primary text-white px-10 py-5 rounded-3xl font-black text-[10px] uppercase tracking-[0.2em] shadow-2xl shadow-primary/30 transition-all hover:-translate-y-1 active:scale-95">Update Vet</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminVets;